import { useEffect, useState, useRef } from "react";
import {
  getUsersApi,
  getRolesApi,
  createUserApi,
  deleteUserApi,
  getUserByIdApi,
  updateUserApi,
} from "../../services/user/userService";

import { getUserId } from "../../utils/authToken";
import toast from "react-hot-toast";

export const useUserViewModel = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  //  PREVENT DOUBLE CALL
  const usersLoaded = useRef(false);
  const rolesLoaded = useRef(false);

  // ================= LOAD USERS =================
  const loadUsers = async () => {
    setLoading(true);

    try {
      const res = await getUsersApi(search, page, 10);

      if (!res?.flag) {
        throw new Error(res?.message || "Failed to load users");
      }

      setUsers(res.items || []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ================= LOAD ROLES =================
  const loadRoles = async () => {
    try {
      const res = await getRolesApi();

      if (!res?.flag) {
        throw new Error(res?.message || "Failed to load roles");
      }

      setRoles(res.items || []);
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // ================= AUTO LOAD USERS =================
  useEffect(() => {
    // first render fix (StrictMode safe)
    if (!usersLoaded.current) {
      usersLoaded.current = true;
      loadUsers();
      return;
    }

    // allow updates for page/search changes
    loadUsers();
  }, [search, page]);

  // ================= AUTO LOAD ROLES =================
  useEffect(() => {
    if (rolesLoaded.current) return;

    rolesLoaded.current = true;
    loadRoles();
  }, []);

  // ================= CREATE USER =================
  const createUser = async (data: any) => {
    try {
      if (!data.firstName?.trim()) throw new Error("First name is required");
      if (!data.lastName?.trim()) throw new Error("Last name is required");
      if (!data.email?.trim()) throw new Error("Email is required");

      if (!data.password || data.password.length < 4) {
        throw new Error("Password must be at least 4 characters");
      }

      const res = await createUserApi(data);

      if (!res?.flag) throw new Error(res.message);

      toast.success("User created");
      await loadUsers();
    } catch (err: any) {
      toast.error(err.message);
      throw err;
    }
  };

  // ================= DELETE USER =================
  const deleteUser = async (id: string) => {
    try {
      const currentUserId = getUserId();

      if (id === currentUserId) {
        throw new Error("You cannot delete your own account");
      }

      const res = await deleteUserApi(id);

      if (!res?.flag) throw new Error(res.message);

      toast.success("User deleted");
      await loadUsers();
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  // ================= GET USER BY ID =================
  const getUserById = async (id: string) => {
    setLoading(true);

    try {
      const res = await getUserByIdApi(id);

      if (!res?.flag) {
        throw new Error(res.message);
      }

      const user = res.item;

      return {
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        phoneNumber: user.phoneNumber,
        roleId: user.roleId,
        password: "",
        confirmPassword: "",
        userTenants:
          user.userTenants?.map((t: any) => ({
            tenantId: t.tenantId,
            isDefault: t.isDefault,
          })) || [],
      };
    } catch (err: any) {
      toast.error(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ================= UPDATE USER =================
  const updateUser = async (form: any) => {
    try {
      if (!form.roleId) throw new Error("Role is required");

      if (!form.userTenants || form.userTenants.length === 0) {
        throw new Error("Select at least one company");
      }

      if (form.password) {
        if (form.password.length < 4) {
          throw new Error("Password must be at least 4 characters");
        }

        if (form.password !== form.confirmPassword) {
          throw new Error("Passwords do not match");
        }
      }

      const payload: any = {
        id: form.id,
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        roleId: form.roleId,
        phoneNumber: form.phoneNumber,
        userTenants: form.userTenants,
      };

      if (form.password) {
        payload.password = form.password;
        payload.confirmPassword = form.confirmPassword;
      }

      const res = await updateUserApi(payload);

      if (!res?.flag) throw new Error(res.message);

      toast.success("User updated");

      await loadUsers();

      return res;
    } catch (err: any) {
      toast.error(err.message);
      throw err;
    }
  };

  return {
    users,
    roles,
    loading,

    search,
    setSearch,

    page,
    setPage,

    createUser,
    deleteUser,
    getUserById,
    updateUser,
  };
};